import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
/* eslint-disable ember/no-runloop */
import { action, computed, set } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismIcon, PrismTooltip } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelMarketInsightDemandLevelIndicator from 'lumen/components/hotel/market-insight/demand-level-indicator';
import HotelMarketInsightTooltipsTooltipSummary from 'lumen/components/hotel/market-insight/tooltips/tooltip-summary';
import type { Levelbucket } from 'lumen/models';
import {
  getWeekendPlotBands,
  tickPositionerDayWeek,
  xValueDifference,
} from 'lumen/utils/charts/chart-utils';
import { addNoDataZone, addZone, NO_DATA_ZONE } from 'lumen/utils/charts/zones';
import {
  ACTIVE_FILTERS,
  DEMAND_FORECAST_SERIES_VISIBILITY_KEY,
  DEMAND_INDEX_LEVEL_BUCKET_IDS,
  LEVEL_TO_DEMAND_COLOR_MAPPING,
  MI_OVERVIEW_COLUMN_CONFIG,
} from 'lumen/utils/constants/market-insight-constants';
import {
  tooltipPositionerForOverview,
  xAxisLabelFormatterForOverview,
} from 'lumen/utils/market-insight/market-insight-helpers';
import { timeout } from 'lumen/utils/waiters';

interface HotelMarketInsightChartsChartMarketDemandSignature {
  Args: {
    isMarketInsightFull: unknown;
    model: unknown;
    onChartClicked: unknown;
    onUpdateChartReference: unknown;
    refDaysAgo: unknown;
    setActiveFilters: unknown;
  };
}

export default class HotelMarketInsightChartsChartMarketDemand extends Component<HotelMarketInsightChartsChartMarketDemandSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare miRateindex: Services['mi-rateindex'];
  @service declare miSelections: Services['mi-selections'];
  @service declare sessionStorage: Services['session-storage'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked seriesVisibility = {
    isPriceStrategyVisible: true,
    isOpportunityVisible: true,
    isWOWChangeVisible: false,
    isSmartCompsetPriceStrategyVisible: false,
  };
  @tracked tooltipDay = undefined;
  @tracked tooltipRateindex = null;

  /**
   * We track hovers over the calendar day here
   * If a user remains here for 2 seconds we'll log an event
   * If they leave sooner, the event will be cancelled
   */
  trackHoverTask = task({ restartable: true }, async () => {
    await timeout('extra-long');

    // tooltipDay can be undefined if the user hovers over a day with missing data
    if (!this.tooltipDay) {
      return;
    }

    // Testing out the new tracking service, eventually one of these ⬆️⬇️ tracking calls will be removed
    this.usageAnalytics.track('Daily Market Demand Previewed', {
      eventTrigger: 'demand-forecast-chart-hover',
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      daysOffsetFromToday: this.tooltipDay.date
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .startOf('day')
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .diff(this.hotelAndMarketService.getLocalTime().startOf('day'), 'day'),
      // @ts-expect-error: Incorrect type
      isOutlier: this.tooltipRateindex?.outliersCount > 0,
    });
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const data = [];

    data.push({
      type: 'column',
      name: 'Demand',
      data: this.formattedChartData.demandData,
    });

    data.push({
      zIndex: 10,
      type: 'line',
      name: 'Price strategy',
      color: 'var(--prism-color-chart-accent-primary-figure-default)',
      zones: this.formattedChartData.priceStrategyZones,
      zoneAxis: 'x',
      connectNulls: true,
      visible: this.seriesVisibility.isPriceStrategyVisible,
      data: this.formattedChartData.priceStrategyData,
      marker: {
        enabled: false,
      },
    });

    data.push({
      zIndex: 20,
      type: 'scatter',
      name: 'Opportunty',
      visible: this.seriesVisibility.isOpportunityVisible,
      data: this.formattedChartData.opportunityData,
      marker: {
        enabled: true,
        lineColor: 'var(--prism-color-chart-accent-primary-figure-emphasis)',
        lineWidth: 3,
        radius: 4,
        fillColor: 'var(--prism-color-elevation-surface)',
      },
    });

    data.push({
      zIndex: 10,
      type: 'line',
      name: 'Smart compset price strategy',
      color: 'var(--prism-color-chart-accent-primary-figure-subdued)',
      zones: this.formattedChartData.smartCompsetPriceStrategyZones,
      zoneAxis: 'x',
      connectNulls: true,
      visible: this.seriesVisibility.isSmartCompsetPriceStrategyVisible,
      data: this.formattedChartData.smartCompsetPriceStrategyData,
      marker: {
        enabled: false,
      },
    });

    data.push({
      zIndex: 20,
      type: 'scatter',
      name: 'WOW',
      color: 'var(--color-market-insight-wow)',
      stroke: 'var(--color-market-insight-wow)',
      visible: this.seriesVisibility.isWOWChangeVisible,
      data: this.formattedChartData.WOWChange,
      states: {
        hover: {
          enabled: false,
        },
      },
    });

    return data;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      chart: {
        marginLeft: 80, // Extra margin because the labels can be long
        zoomType: 'x',
        resetZoomButton: {
          theme: {
            display: 'none',
          },
        },
      },
      title: {
        text: 'market-demand', // title used for the events only
      },
      xAxis: {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        tickPositioner() {
          return tickPositionerDayWeek(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.dataMin,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.dataMax,
            that.miSelections.isDailyOverview,
          );
        },
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            return xAxisLabelFormatterForOverview(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              this.value,
              that.miSelections.isDailyOverview,
              that.intl,
            );
          },
        },
        plotBands: this.weekendPlotBands,
        crosshair: true,
        events: {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          afterSetExtremes(e) {
            // We need to use afterSetExtremes instead of set extremes
            // setExtremes executes the moment you let go of the zoom
            // afterSetExtremes only runs after the chart has redrawn
            // This behaviour is better, since we can get unexpected errors if we change chart options during a redraw
            // This does mean we need to check the trigger of the event, since this is not only after a zoom
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (e.trigger && e.trigger === 'zoom') {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              let zoomStartDate = dayjs.utc(e.min);
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              let zoomEndDate = dayjs.utc(e.max);

              const filterId = ACTIVE_FILTERS.RANGE;

              if (that.miSelections.isWeeklyOverview) {
                // If it's weekly make sure its a weekly range (from a monday till a sunday)
                zoomStartDate = zoomStartDate.startOf('isoWeek');
                zoomEndDate = zoomEndDate.endOf('isoWeek');
              }

              zoomStartDate =
                that.miSelections.getStartDateInRange(zoomStartDate);
              zoomEndDate = that.miSelections.getEndDateInRange(zoomEndDate);

              const filterValue = `${zoomStartDate.format('YYYY-MM-DD')}:${zoomEndDate.format(
                'YYYY-MM-DD',
              )}`;

              // @ts-expect-error: Incorrect type
              that.args.setActiveFilters(filterId, filterValue);
            }
          },
        },
      },
      yAxis: {
        labels: {
          enabled: false,
        },
        title: {
          enabled: false,
        },
        gridLineColor: 'transparent', // We set plotlines already
        softMax: this.yAxisMax, // puts a max on the chart so you can see all plotLines (very low -> very high)
        min: -0.15, // added to make sure the graph doesn't jump when WOW change demand is toggled/untoggled
        endOnTick: false, // Needed to put this one and the next one on false to prevent highcharts adding a margin
        startOnTick: false,
        plotLines: this.plotLinesForDemandLevelBuckets,
      },
      tooltip: {
        hideDelay: 0.2,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        shared: true,
        outside: true, // allows the tooltip to render outside the chart's SVG element box (required because else the chart tooltip can get cut off)
        shape: 'square',
        distance: 20,

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        positioner(labelWidth, labelHeight, point) {
          return tooltipPositionerForOverview(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            labelWidth,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            labelHeight,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            point,
            that.miSelections.isWeeklyOverview,
            0,
            // @ts-expect-error: Incorrect type
            that.args.isMarketInsightFull,
          );
        },

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { day } = this.point
            ? // @ts-expect-error: Incorrect type
              this.point
            : // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              this.points.find((point) => point.series.name === 'Demand').point;

          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          that.trackHoverTask.perform();

          // Depending on the aggregation the user is looking at, we'll need to look at daily or weekly summaries for our missing data check
          const hasDataForDay =
            that.miSelections.isDailyOverview &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !day.mostRecentDailySummary?.isDestinationDemandIndexMissing;
          const hasDataForWeek =
            that.miSelections.isWeeklyOverview &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !day.mostRecentWeeklySummary?.isDestinationDemandIndexMissing;

          if (hasDataForDay || hasDataForWeek) {
            // Necessary to get correct data for tooltips, needs to be in separate runloop
            begin();
            set(that, 'tooltipDay', day);
            set(that, 'tooltipRateindex', that.getRateIndexDataOfOwnHotel(day));
            end();
            // @ts-expect-error: Incorrect type
            return document.getElementById('dummy-tooltip-for-market-demand')
              .innerHTML;
          }

          // We don't want to show the MI day tooltip in case of missing market data. Show the same no data explanation message as in the calendar
          set(that, 'tooltipDay', undefined);
          set(that, 'tooltipRateindex', null);
          // Use Ember-tooltip default background color
          return `<div class="info-tooltip" style="padding:6px 10px; color: var(--prism-color-text-inverse); background: var(--prism-color-background-inverse);">${that.intl.t(
            'market_insight.calendar_tooltip.no_data_message',
          )}</div>`;
        },
        useHTML: true,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: MI_OVERVIEW_COLUMN_CONFIG,
        series: {
          cursor: 'pointer',
          animation: false,
          events: {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            click(e) {
              if (that.miSelections.isDailyOverview) {
                // @ts-expect-error: Incorrect type
                that.args.onChartClicked(e);

                // Testing out the new tracking service, eventually one of these ⬆️⬇️ tracking calls will be removed
                const dateClicked = that.hotelAndMarketService.toLocalTime(
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                  e.point.x,
                );
                that.usageAnalytics.track('Daily Market Demand Viewed', {
                  eventTrigger: 'demand-forecast-chart',
                  daysOffsetFromToday: dateClicked.diff(
                    that.hotelAndMarketService.getLocalTime().startOf('day'),
                    'days',
                  ),
                  // @ts-expect-error: Incorrect type
                  isOutlier: that.tooltipRateindex?.outliersCount > 0,
                });
              }

              // @ts-expect-error: Incorrect type
              that.args.onChartClicked(e);
            },
          },
        },
      },
    };
  }

  /**
   * get the demand level buckets used for th ey axis and colouring of the bars
   * Already sorts them by level (from very low to bery high)
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevelBuckets() {
    const bucketToUse = this.store.peekRecord<Levelbucket>(
      'levelbucket',
      DEMAND_INDEX_LEVEL_BUCKET_IDS.YEARLY,
    );

    // Return empty array as fallback in case of undefined buckets. Mainly to avoid JS errors, should never be the case in normal conditions!
    return bucketToUse?.levels?.slice().sort((a, b) => a.level - b.level) ?? [];
  }

  /**
   * This return the data for each of the 3 series we show in the chart
   */
  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChartData() {
    // @ts-expect-error: Incorrect type
    const demandData = [];
    // @ts-expect-error: Incorrect type
    const priceStrategyData = [];
    // @ts-expect-error: Incorrect type
    const priceStrategyZones = [];
    // @ts-expect-error: Incorrect type
    const opportunityData = [];
    // @ts-expect-error: Incorrect type
    const smartCompsetPriceStrategyData = [];
    // @ts-expect-error: Incorrect type
    const smartCompsetPriceStrategyZones = [];
    // @ts-expect-error: Incorrect type
    const WOWChange = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.sortedAndFilteredSummaries.forEach((summary) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { day } = summary;
      const rateIndexData = this.getRateIndexDataOfOwnHotel(day);
      const formattedDay = this.hotelAndMarketService.toLocalUnixTime(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        day.date.unix(),
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const rateIndex = summary.isDemandIndexMissing
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          summary.destinationDemandIndex
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          summary.demandIndex;

      let demandBarColor = null;
      let demandBorderColor = 'var(--prism-color-background-neutral-default)';

      // Case1: WOW demand change is toggled on : Only highlight the bars that has demand change, make the rest grey
      // Case2: WOW demand change is untoggled : No effect on the demand level bars
      if (this.seriesVisibility.isWOWChangeVisible) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (summary?.hasDemandChange) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          demandBarColor =
            // @ts-expect-error: Incorrect type
            LEVEL_TO_DEMAND_COLOR_MAPPING[
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              summary.demandLevelColorScaleNumber || 0
            ];
          demandBorderColor =
            'var(--prism-color-interactive-border-neutral-pressed)';
        } else {
          demandBarColor = 'var(--prism-color-chart-neutral-figure-empty)';
          demandBorderColor = 'var(--prism-color-interactive-icon-disabled)';
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        demandBarColor =
          // @ts-expect-error: Incorrect type
          LEVEL_TO_DEMAND_COLOR_MAPPING[
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            summary.demandLevelColorScaleNumber || 0
          ];
      }

      demandData.push({
        x: formattedDay,
        // Had to correct the values with the normalizefactor to make sure they are all positive
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        y: (rateIndex || 0) + this.normalizeFactor,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        color: demandBarColor,
        borderColor: demandBorderColor,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        day, // we pass the day for the tooltip
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        className: summary.isDestinationDemandIndexMissing ? 'disabled' : '',
      });

      if (
        rateIndexData.hasCorrectRateIndexValue &&
        this.args.isMarketInsightFull
      ) {
        // only show a price if there is a value
        // Checking if there are outliers available for this day.
        const isOpportunity = rateIndexData?.outliersCount > 0;

        priceStrategyData.push({
          x: formattedDay,
          // Had to correct the values with the normalizefactor to make sure they are all positive
          y: rateIndexData.value + this.normalizeFactor,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          day, // we pass the day for the tooltip
        });

        if (isOpportunity) {
          opportunityData.push({
            x: formattedDay,
            y: rateIndexData.value + this.normalizeFactor,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            day,
          });
        }
      }

      if (this.args.isMarketInsightFull) {
        smartCompsetPriceStrategyData.push({
          x: formattedDay,
          // Had to correct the values with the normalizefactor to make sure they are all positive
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          y: summary.smartCompsetRateIndex + this.normalizeFactor,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          day,
        });
      }

      // Adding no data zones for priceStrategy series when the own hotel is sold out
      // The dotted line can only be visualised when the sold out days are surrounded by non null values
      if (
        rateIndexData.message !== 'rates.soldout' &&
        rateIndexData.message !== 'rateindex.soldout'
      ) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const lastZone = priceStrategyZones[priceStrategyZones.length - 1];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (lastZone && lastZone.dashStyle === NO_DATA_ZONE.dashStyle) {
          // If the last zone was a nodata zone, add a new zone
          addZone(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            priceStrategyZones,
            formattedDay + this.unixDifferenceBetweenIndexes,
          );
        }
      } else {
        addNoDataZone(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          priceStrategyZones,
          { x: formattedDay - this.unixDifferenceBetweenIndexes },
          { x: formattedDay + this.unixDifferenceBetweenIndexes },
          formattedDay,
        ); // Zone goes from the previous point to the next point
      }

      // Adding no data zones for smartCompsetPriceStrategy series when the sub has its entire compset being sold out
      // The dotted line can only be visualised when the sold out days are surrounded by non null values
      if (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        summary.rateIndexMessage !== 'summary.smart_compset_rate_index_missing'
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const lastZone =
          // @ts-expect-error: Incorrect type
          smartCompsetPriceStrategyZones[
            smartCompsetPriceStrategyZones.length - 1
          ];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (lastZone && lastZone.dashStyle === NO_DATA_ZONE.dashStyle) {
          // If the last zone was a nodata zone, add a new zone
          addZone(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            smartCompsetPriceStrategyZones,
            formattedDay + this.unixDifferenceBetweenIndexes,
          );
        }
      } else {
        addNoDataZone(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          smartCompsetPriceStrategyZones,
          { x: formattedDay - this.unixDifferenceBetweenIndexes },
          { x: formattedDay + this.unixDifferenceBetweenIndexes },
          formattedDay,
        ); // Zone goes from the previous point to the next point
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (summary?.hasDemandChange) {
        const hasDemandIncrease =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          summary.demandLevelColorScaleNumber >
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          summary.demandLevelColorScaleNumberRefPeriod;
        WOWChange.push({
          x: formattedDay,
          y: -0.075,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          day,
          marker: {
            symbol: hasDemandIncrease ? 'arrowUp' : 'arrowDown',
            lineColor: 'var(--prism-color-text-neutral-default)',
            lineWidth: 2,
            fillColor: 'var(--prism-color-elevation-surface)',
          },
        });
      }
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    const minDemand = Math.min(...demandData.map((d) => d.y));
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    const maxDemand = Math.max(...demandData.map((d) => d.y));
    const demandOffset = maxDemand - minDemand;

    // Change the y-value of the demand change arrows in case there is a high difference between the highest demand and the lowest demand on the chart
    // This is done to prevent the demand change arrows from overlapping with the bars in the bar chart
    if (demandOffset > 1) {
      // @ts-expect-error: Incorrect type
      WOWChange.forEach((change) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        change.y = -0.125;
      });
    }

    return {
      // @ts-expect-error: Incorrect type
      demandData,
      // @ts-expect-error: Incorrect type
      priceStrategyData,
      // @ts-expect-error: Incorrect type
      priceStrategyZones,
      // @ts-expect-error: Incorrect type
      opportunityData,
      // @ts-expect-error: Incorrect type
      smartCompsetPriceStrategyData,
      // @ts-expect-error: Incorrect type
      smartCompsetPriceStrategyZones,
      // @ts-expect-error: Incorrect type
      WOWChange,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasSelectedDateRangePriceOpportunities() {
    return Boolean(this.formattedChartData.opportunityData?.length);
  }

  // We disable the WOW demand change legend item in case there is not demand change in the selected period
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasWOWChangeInSelectedRange() {
    return this.formattedChartData.WOWChange.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPriceOpportunitiesLegendVisible() {
    return (
      this.seriesVisibility.isOpportunityVisible &&
      this.hasSelectedDateRangePriceOpportunities
    );
  }

  /**
   * The buckets range from negative numbers to positive ones
   * The bars need to be positive so we add a normalisation factor that makes every bucket > 0
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get normalizeFactor() {
    const levels = this.demandLevelBuckets;

    // The min is either 0.6, if the first bucket is lower though we use that
    if (levels.length && levels[0]!.lower_bound < 0) {
      return Math.max(Math.abs(levels[0]!.lower_bound), 0.4);
    }

    return 0.4;
  }

  /**
   * Here we determine where the plotlines need to be. They start at the lower bound of each bucket
   * We also add plotlines for the labels. These are transparent lines with the only purpose of centering the labels
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get plotLinesForDemandLevelBuckets() {
    const levels = this.demandLevelBuckets;
    // @ts-expect-error: Incorrect type
    const plotLines = [];

    levels.forEach((level, index) => {
      const isVeryLow = level.level === 1;
      const text = this.intl.t(`market_insight.rate_level.level${level.level}`);
      let value = 0;
      let top = 0;

      // The very low level line should be at 0
      if (!isVeryLow) {
        value = level.lower_bound + this.normalizeFactor;
      }

      // If we reached the end, use the max value as the top
      if (index + 1 === levels.length) {
        top = this.yAxisMax!;
      } else {
        top = levels[index + 1]!.lower_bound + this.normalizeFactor;
      }

      // Value for the label line
      const center = (value + top) / 2;

      // This plotline is a line without the label, showing the start of the bucket
      plotLines.push({
        color: 'var(--prism-color-border-neutral-default)',
        value,
      });

      // This plotline is a label without a line, used for centering the labels in a bucket
      plotLines.push({
        color: 'transparent',
        value: center,
        label: {
          text,
          textAlign: 'right',
          align: 'left',
          style: {
            color: 'var(--prism-color-text-neutral-subdued)',
          },
          x: -10, // Custom positioning because highcharts puts it slightly above the line
          y: 3,
        },
      });
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return plotLines;
  }

  /**
   * We only need the latest summaries for the chart
   * We also sort them by day since it is required for highcharts
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedAndFilteredSummaries() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.summaries
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .filter((summary) => summary.isLatest)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort((a, b) => a.day.integerDate - b.day.integerDate)
    );
  }

  @computed('args.model.data.summaries')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get summaries() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.model?.data?.summaries;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unixDifferenceBetweenIndexes() {
    return xValueDifference(this.miSelections.isDailyOverview) * 1000;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get weekendPlotBands() {
    if (this.miSelections.isDailyOverview) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const summaries = this.sortedAndFilteredSummaries ?? [];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const startDate = summaries[0]?.day?.date;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const endDate = summaries[summaries.length - 1]?.day?.date;

      return getWeekendPlotBands(
        this.hotelAndMarketService,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        startDate,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        endDate,
      );
    }

    return [];
  }

  /**
   * The max of the yAxis is lower bound of the very high level + (lower bound very high level - lower bound high level) + the normalizeFactor
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxisMax() {
    const levels = this.demandLevelBuckets;

    if (levels.length) {
      const veryHighLevel = levels[levels.length - 1]!;
      const highLevel = levels[levels.length - 2]!;

      const offset = veryHighLevel.lower_bound - highLevel.lower_bound;

      return veryHighLevel.lower_bound + this.normalizeFactor + offset;
    }

    return null; // Highcharts default in case of missing levels
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    if (
      JSON.parse(
        // @ts-expect-error: Incorrect type
        this.sessionStorage.getItem(DEMAND_FORECAST_SERIES_VISIBILITY_KEY),
      )
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.seriesVisibility = JSON.parse(
        // @ts-expect-error: Incorrect type
        this.sessionStorage.getItem(DEMAND_FORECAST_SERIES_VISIBILITY_KEY),
      );
    }

    // In case isWOWChangeVisible is true in the session storage but there is actually no WOW change in the new selected range -> Set the isWOWChangeVisible to false
    if (!this.hasWOWChangeInSelectedRange) {
      this.seriesVisibility.isWOWChangeVisible = false;
    }
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getRateIndexDataOfOwnHotel(day) {
    return this.miRateindex.getRateIndexForCurrentHotelAndLatestExtractDate(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      day,
      this.miSelections.dateAggregation,
    );
  }

  @action
  // @ts-expect-error: Incorrect type
  toggleSeries(seriesProperty): void {
    if (seriesProperty === 'isPriceStrategyVisible') {
      this.seriesVisibility.isPriceStrategyVisible =
        !this.seriesVisibility.isPriceStrategyVisible;
    }
    if (
      seriesProperty === 'isOpportunityVisible' &&
      this.hasSelectedDateRangePriceOpportunities
    ) {
      this.seriesVisibility.isOpportunityVisible =
        !this.seriesVisibility.isOpportunityVisible;
    }
    if (seriesProperty === 'isWOWChangeVisible') {
      this.seriesVisibility.isWOWChangeVisible =
        !this.seriesVisibility.isWOWChangeVisible;
    }
    if (seriesProperty === 'isSmartCompsetPriceStrategyVisible') {
      this.seriesVisibility.isSmartCompsetPriceStrategyVisible =
        !this.seriesVisibility.isSmartCompsetPriceStrategyVisible;
    }

    // update tracked object
    // eslint-disable-next-line no-self-assign
    this.seriesVisibility = this.seriesVisibility;

    // persist the toggle stage of custom legend item in session storage
    this.sessionStorage.setItem(
      DEMAND_FORECAST_SERIES_VISIBILITY_KEY,
      JSON.stringify(this.seriesVisibility),
    );
  }

  <template>
    {{! @glint-nocheck }}
    <GeneralChartsOtaChart
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
      @onUpdateChartReference={{@onUpdateChartReference}}
    />

    <div class="market-demand-legend flex items-center justify-center">
      <div class="chart-legend__item">
        <div
          class="demand-level-indicator-wrapper flex flex-row items-center justify-center"
        >
          <HotelMarketInsightDemandLevelIndicator />
          <p class="text-200--medium">{{t
              "market_insight.overview.demand_level"
            }}</p>
          <GeneralIconWithTooltip
            @side="top"
            @tooltipContent={{t
              "market_insight.kpi_explanation.submarket_demand"
            }}
            @tooltipTitle={{t "market_insight.overview.demand_level"}}
          />
        </div>
      </div>
      {{#if @isMarketInsightFull}}
        <div class="chart-legend__item flex flex-row">
          <button
            class="button--unstyled flex items-center justify-center
              {{unless
                this.seriesVisibility.isPriceStrategyVisible
                'legend-hidden'
              }}
              ic-product-tour-price-level-legend"
            data-test-id="chart-legend-price-level"
            type="button"
            {{on "click" (fn this.toggleSeries "isPriceStrategyVisible")}}
          >
            <div
              class="chart-legend-item__marker chart-legend__item__price-strategy line"
            ></div>
            <p class="text-200--medium">
              {{t "market_insight.price_level"}}
            </p>
          </button>
          <GeneralIconWithTooltip
            @side="top"
            @tooltipContent={{t
              "market_insight.kpi_explanation.price_level_overview"
            }}
            @tooltipTitle={{t "market_insight.price_level"}}
          />
        </div>
        <div class="chart-legend__item flex flex-row">
          <button
            class="button--unstyled flex items-center justify-center
              {{unless this.isPriceOpportunitiesLegendVisible 'legend-hidden'}}
              ic-product-tour-opportunity-legend"
            data-test-id="chart-legend-price-opportunity"
            type="button"
            {{on "click" (fn this.toggleSeries "isOpportunityVisible")}}
          >
            <div
              class="chart-legend-item__marker chart-legend__item__price-opportunity"
            ></div>
            <p class="text-200--medium">
              {{t "market_insight.detail_pane.section_title.price_opportunity"}}
            </p>
            {{#unless this.hasSelectedDateRangePriceOpportunities}}
              <PrismTooltip
                @side="top"
                @text={{t "market_insight.overview.no_opportunities"}}
              />
            {{/unless}}
          </button>
          {{#if this.miSelections.isDailyOverview}}
            <GeneralIconWithTooltip
              @side="top"
              @tooltipContent={{t
                "market_insight.kpi_explanation.date_aggregation_day.price_opportunity_overview"
              }}
              @tooltipTitle={{t "market_insight.price_opportunity"}}
            />
          {{else}}
            <GeneralIconWithTooltip
              @side="top"
              @tooltipContent={{t
                "market_insight.kpi_explanation.date_aggregation_week.price_opportunity_overview"
              }}
              @tooltipTitle={{t "market_insight.price_opportunity"}}
            />
          {{/if}}
        </div>
        <div class="chart-legend__item flex flex-row">
          <button
            class="button--unstyled flex items-center justify-center
              {{unless
                this.seriesVisibility.isSmartCompsetPriceStrategyVisible
                'legend-hidden'
              }}
              ic-product-tour-smart-compset-price-level-legend"
            data-test-id="chart-legend-compset-price"
            type="button"
            {{on
              "click"
              (fn this.toggleSeries "isSmartCompsetPriceStrategyVisible")
            }}
          >
            <div
              class="chart-legend-item__marker chart-legend__item__smart-compset-price-strategy line"
            ></div>
            <p class="text-200--medium">
              {{t "market_insight.overview.smart_compset_level"}}
            </p>
          </button>
          <GeneralIconWithTooltip
            @side="top"
            @tooltipContent={{t
              "market_insight.overview.tooltip.smart_compset_strategy.body"
            }}
            @tooltipTitle={{t "market_insight.overview.smart_compset_level"}}
          />
        </div>
      {{/if}}
      <div
        class="chart-legend__item wow-change flex flex-row"
        data-test-id="chart-legend-demand-forecast-item"
      >
        <div>
          <button
            class="button--unstyled flex items-center justify-center
              {{unless
                this.seriesVisibility.isWOWChangeVisible
                'legend-hidden'
              }}
              ic-product-tour-demand-change-legend"
            data-test-id="chart-legend-demand-forecast-button"
            disabled={{not this.hasWOWChangeInSelectedRange}}
            type="button"
            {{on "click" (fn this.toggleSeries "isWOWChangeVisible")}}
          >
            <div class="flex items-center">
              <PrismIcon @color="inherit" @name="arrow-up" @size="small" />
              <PrismIcon @color="inherit" @name="arrow-down" @size="small" />
            </div>
            <p class="text-200--medium">
              {{t "market_insight.demand_change"}}
              {{t "general.daysago" count=@refDaysAgo}}
            </p>
          </button>
          {{#unless this.hasWOWChangeInSelectedRange}}
            {{#if this.miSelections.isDailyOverview}}
              <PrismTooltip
                @text={{t
                  "market_insight.no_wow_change.date_aggregation_day.message"
                  count=@refDaysAgo
                }}
              />
            {{else}}
              <PrismTooltip
                @text={{t
                  "market_insight.no_wow_change.date_aggregation_week.message"
                }}
              />
            {{/if}}
          {{/unless}}
        </div>
        <GeneralIconWithTooltip
          @side="top"
          @tooltipContent={{t
            "market_insight.kpi_explanation.wowchange"
            count=@refDaysAgo
          }}
          @tooltipTitle={{t "market_insight.demand_change"}}
        />
      </div>
    </div>

    <div class="hidden" id="dummy-tooltip-for-market-demand">
      {{#if this.tooltipDay}}
        <div class="tooltip-market-insight">
          <HotelMarketInsightTooltipsTooltipSummary
            @day={{this.tooltipDay}}
            @isDailyTooltip={{this.miSelections.isDailyOverview}}
            @isMarketInsightFull={{@isMarketInsightFull}}
            @rateindex={{this.tooltipRateindex}}
            @refDaysAgo={{@refDaysAgo}}
          />
        </div>
      {{/if}}
    </div>
  </template>
}
